import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { stepModeEnum, truckModeEnum } from '../../constants';
import { useCreateDailyTransferMutation, useUpdateDailyTransferMutation } from '../../hooks/mutations/useDailyTransfersMutation';
import { useBoolean } from '../../hooks/useBoolean';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTransfer } from '../../services/apiSLTP/dailyReports/dailyTransfers';
import { DailyTruckPlanned } from '../../services/apiSLTP/dailyReports/dailyTrucksPlanned';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DroppableCell from '../dnd/DroppableCell';
import AssignTruckPlannedModal from '../modals/operations/planning/AssignTruckPlannedModal';
import ManageDailyTransferModal from '../modals/operations/planning/ManageDailyTransferModal';
import ManageDailyTruckPlannedModal from '../modals/operations/planning/ManageDailyTruckPlannedModal';
import IconButton from '../ui/IconButton';
import TruckPlannedItem from './TruckPlannedItem';
import {
    useCreateDailyTruckPlannedMutation,
    useDeleteDailyTruckPlannedMutation,
    useUpdateDailyTruckPlannedMutation,
} from '../../hooks/mutations/useDailyTrucksPlannedMutation';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface TrucksPlannedCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    refetch?: () => void;
    hiddenPlusIcon?: boolean;
    group?: number;
    isTruckSelectable?: boolean;
    stepMode?: number;
    currentDate: Date;
}

const TrucksPlannedCell: React.FC<TrucksPlannedCellProps> = ({
    activity,
    activities,
    disabled = false,
    refetch = () => {},
    hiddenPlusIcon = false,
    group,
    isTruckSelectable = true,
    stepMode = stepModeEnum.PLANNING,
    currentDate,
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.TRUCKSPLANNED;
    const [selectedTransfer, setSelectedTransfer] = React.useState<DailyTransfer | null>(null);

    const { mutate: updateDailyTruckPlanned } = useUpdateDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: deleteDailyTruckPlanned } = useDeleteDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: createDailyTruckPlanned } = useCreateDailyTruckPlannedMutation({ onSuccess: refetch });
    const { mutate: createDailyTransfer } = useCreateDailyTransferMutation({ onSuccess: refetch });

    const {
        selectedItem: selectedTruckPlanned,
        manageModal,
        openModal,
        handleMove,
    } = useDailyResource<DailyTruckPlanned>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyTruckPlanned({ DailyTruckPlannedId: itemId, data: { activityId: toLineId }, queries: { autoTruck: true } });
        },
        onDelete: async (itemId) => {
            deleteDailyTruckPlanned(itemId);
        },
        onDuplicate: async (truckPlanned) => {
            createDailyTruckPlanned(
                {
                    activityId: activity._id,
                    trucksAmount: truckPlanned.trucksAmount,
                    departureLoadNatureId: truckPlanned.departureLoadNatureId?._id,
                    arrivalLoadNatureId: truckPlanned.arrivalLoadNatureId?._id,
                    departureDumpId: truckPlanned.departureDumpId?._id,
                    arrivalDumpId: truckPlanned.arrivalDumpId?._id,
                    departureQuarryId: truckPlanned.departureQuarryId?._id,
                    arrivalQuarryId: truckPlanned.arrivalQuarryId?._id,
                    departureSiteId: truckPlanned.departureSiteId?._id,
                    arrivalSiteId: truckPlanned.arrivalSiteId?._id,
                    mode: truckPlanned.mode,
                    ridesAmount: truckPlanned.ridesAmount,
                    truckTypeId: truckPlanned.truckTypeId._id,
                    truckCompanyId: truckPlanned.truckCompanyId?._id,
                    observations: truckPlanned.observations,
                    truckId: truckPlanned.truckId?._id,
                },
                {
                    onSuccess: (data) => {
                        for (const transfer of truckPlanned.transfers) {
                            createDailyTransfer({
                                dailyTrucksPlannedId: data.data._id,
                                state: transfer.state,
                                machineId: transfer.machineId?._id,
                                quarryId: transfer.quarryId?._id,
                                rentalCompanyId: transfer.rentalCompanyId?._id,
                                rentalName: transfer.rentalName,
                                siteId: transfer.siteId?._id,
                                toolId: transfer.toolId?._id,
                            });
                        }
                    },
                }
            );
        },
    });

    const assignModal = useBoolean(false);
    const transferModal = useBoolean(false);

    const { mutate: updateDailyTransferPlanned } = useUpdateDailyTransferMutation({
        onSettled: () => refetch(),
    });

    const handleTransferPlannedChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            updateDailyTransferPlanned(
                { DailyTransferId: fromItemId, data: { dailyTrucksPlannedId: toLineId } },
                {
                    onSuccess: async () => {
                        const tmpTruckPlanned = activities.flatMap((v) => v.dailyTrucksPlanned).find((v) => v._id === fromLineId);

                        if (tmpTruckPlanned && tmpTruckPlanned.mode === truckModeEnum.TRANSFER && tmpTruckPlanned.transfers.length <= 1) {
                            await deleteDailyTruckPlanned(fromLineId);
                        }
                    },
                }
            );
        } catch (error) {
            console.error('Error updating transfer:', error);
        }
    };

    const sortedTrucksPlanned = activity.dailyTrucksPlanned
        .filter((v) => group === undefined || v.truckTypeId.group === group)
        .sort((a, b) => {
            if ((a.rank || 1) < (b.rank || 1)) return -1;
            if ((a.rank || 1) > (b.rank || 1)) return 1;
            if ((a.mode || -1) < (b.mode || -1)) return -1;
            if ((a.mode || -1) > (b.mode || -1)) return 1;
            return 0;
        });

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, itemId, toLineId) => {
                    handleMove(fromLineId, itemId, toLineId);
                }}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addRoad"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTrucksRides'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {sortedTrucksPlanned.map((truckPlanned, index) => (
                            <TruckPlannedItem
                                key={truckPlanned._id}
                                truckPlanned={truckPlanned}
                                index={index}
                                activityId={activity._id}
                                activityReportTruckGroup={activity.reportId.truckId?.truckTypeId.group}
                                stepMode={stepMode}
                                disabled={disabled}
                                refetch={refetch}
                                onEdit={() => openModal(truckPlanned)}
                                onAssign={() => {
                                    openModal(truckPlanned);
                                    assignModal.setTrue();
                                }}
                                onTransferEdit={(transfer) => {
                                    setSelectedTransfer(transfer);
                                    openModal(truckPlanned);
                                    transferModal.setTrue();
                                }}
                                onTransferMove={handleTransferPlannedChange}
                                activities={activities}
                                isSpecial={activity.reportId.userId.isSpecial}
                            />
                        ))}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && !assignModal.value && !transferModal.value && (
                <ManageDailyTruckPlannedModal
                    open={manageModal.value}
                    activity={activity}
                    handleClose={manageModal.setFalse}
                    refetch={refetch}
                    truckPlannedId={selectedTruckPlanned?._id || ''}
                    truckType={group}
                    isTruckSelectable={isTruckSelectable}
                    stepMode={stepMode}
                />
            )}

            {assignModal.value && selectedTruckPlanned && (
                <AssignTruckPlannedModal
                    dailyTruckPlanned={selectedTruckPlanned}
                    open={assignModal.value}
                    handleClose={() => {
                        assignModal.setFalse();
                        manageModal.setFalse();
                    }}
                    refetch={refetch}
                    activities={activities}
                    currentDate={currentDate}
                />
            )}

            {transferModal.value && selectedTruckPlanned && (
                <ManageDailyTransferModal
                    handleClose={() => {
                        transferModal.setFalse();
                        manageModal.setFalse();
                    }}
                    open={transferModal.value}
                    refetch={refetch}
                    truckPlannedId={selectedTruckPlanned._id}
                    transfer={selectedTransfer}
                    siteName={activity.siteId.name}
                />
            )}
        </Box>
    );
};

export default TrucksPlannedCell;
